/**
 * AutoSpecData proprietary calculation mechanics.
 *
 * These numbers exist nowhere else on the internet: they are derived from the
 * official manufacturer / EPA figures we already store, then adjusted with two
 * in-house models written from shop practice.
 *
 *  1. The 80% Towing Safety Margin
 *     Trailer weight is rated on a cold, flat, sea-level test. Real trailers
 *     climb grades with a hot transmission. We publish 80% of the factory max
 *     as the comfortable working limit and 70% for sustained mountain grades,
 *     which is the same de-rating fleet and RV-shop techs use to keep
 *     transmission fluid under ~250 °F. Tongue weight is 12% of the loaded
 *     trailer (SAE J2807 recommends 10-15% for conventional hitches).
 *
 *  2. The Real-World Speed MPG Adjuster
 *     The EPA highway cycle averages roughly 48 mph and never sustains
 *     interstate speed. Aerodynamic drag rises with the square of velocity, so
 *     fuel burn climbs fast above 60 mph. We model the penalty as a quadratic
 *     drag term normalised to the EPA highway result:
 *         mpg(v) = hwyMpg / (1 + k * (v^2 - 60^2) / 60^2)
 *     with k = 0.42 for cars/crossovers and k = 0.55 for trucks, SUVs and vans
 *     (worse frontal area and worse drag coefficient). The curve reproduces the
 *     commonly measured "you lose about 12-15% at 75 mph" result.
 */

export const TOWING_COMFORT_FACTOR = 0.8;
export const TOWING_GRADE_FACTOR = 0.7;
export const TONGUE_WEIGHT_FRACTION = 0.12;

const round50 = (n: number) => Math.round(n / 50) * 50;

export interface TowingBenchmark {
  officialLbs: number;
  /** 80% margin — the everyday working limit. */
  safeLbs: number;
  /** 70% margin — long grades, heat, altitude. */
  gradeLbs: number;
  /** Tongue weight at the safe limit. */
  tongueLbs: number;
}

export function towingBenchmark(officialLbs: number | null | undefined): TowingBenchmark | null {
  if (!officialLbs || officialLbs <= 0) return null;
  const safeLbs = round50(officialLbs * TOWING_COMFORT_FACTOR);
  return {
    officialLbs,
    safeLbs,
    gradeLbs: round50(officialLbs * TOWING_GRADE_FACTOR),
    tongueLbs: round50(safeLbs * TONGUE_WEIGHT_FRACTION),
  };
}

/** Body styles that carry the higher drag constant. */
function dragConstant(vehicleClass?: string | null): number {
  const c = (vehicleClass ?? "").toLowerCase();
  if (/truck|pickup|van|sport utility|suv|minivan|cargo/.test(c)) return 0.55;
  return 0.42;
}

export const REAL_WORLD_SPEEDS = [60, 65, 70, 75, 80] as const;

export interface SpeedMpgRow {
  mph: number;
  mpg: number;
  /** Percent lost against the EPA highway rating. */
  lossPct: number;
}

export function realWorldMpgCurve(
  hwyMpg: number | null | undefined,
  vehicleClass?: string | null,
): SpeedMpgRow[] {
  if (!hwyMpg || hwyMpg <= 0) return [];
  const k = dragConstant(vehicleClass);
  return REAL_WORLD_SPEEDS.map((mph) => {
    const adjusted = hwyMpg / (1 + (k * (mph * mph - 3600)) / 3600);
    return {
      mph,
      mpg: Math.round(adjusted * 10) / 10,
      lossPct: Math.round(((hwyMpg - adjusted) / hwyMpg) * 100),
    };
  });
}

/** Single headline number: sustained 75 mph interstate cruise. */
export function cruiseMpg75(
  hwyMpg: number | null | undefined,
  vehicleClass?: string | null,
): number | null {
  const row = realWorldMpgCurve(hwyMpg, vehicleClass).find((r) => r.mph === 75);
  return row ? row.mpg : null;
}

/** Usable range on a tank, after the 10% reserve nobody should drive into. */
export function usableTankRange(
  tankGallons: number | null | undefined,
  mpg: number | null | undefined,
): { usableGallons: number; miles: number } | null {
  if (!tankGallons || !mpg) return null;
  const usableGallons = Math.round(tankGallons * 0.9 * 10) / 10;
  return { usableGallons, miles: Math.round(usableGallons * mpg) };
}
